import { useEffect } from 'react'
import BigNumber from 'bignumber.js'
import { useWeb3React } from '@web3-react/core'
import { useSelector } from 'react-redux'
import { useAppDispatch } from 'state'
import { farmsConfig, vaultsConfig } from 'config/constants'
import { simpleRpcProvider } from 'utils/providers'
import { getBalanceAmount } from 'utils/formatBalance'
import { BIG_ZERO } from 'utils/bigNumber'
import useRefresh from 'hooks/useRefresh'
import { filterFarmsByQuoteToken } from 'utils/farmsPriceHelpers'
import {
  fetchFarmsPublicDataAsync,
  fetchPoolsPublicDataAsync,
  fetchPoolsUserDataAsync,
  setBlock,
  fetchPublicDataFromApi,
  fetchPresalePublicDataAsync,
  fetchUserPresaleDataAsync,
  fetchVaultsPublicDataAsync,
  fetchVaultUserDataAsync,
  fetchMultiPoolPublicDataAsync,
  fetchMultiPoolUserDataAsync,
  fetchLaunchpadPublicDataAsync,
  fetchLaunchpadUserDataAsync,
  fetchSeriesesPublicDataAsync,
  fetchSeriesesUserDataAsync,
  fetchTokenBalancesUserDataAsync,
  fetchWheelPublicDataAsync,
  fetchWheelUserDataAsync,
  fetchUserCollectionsDataAsync,
} from './actions'
import {
  State,
  Farm,
  Pool,
  FarmsState,
  PresaleState,
  VaultsState,
  SeriesesState,
  MultiPoolState,
  LaunchpadState,
  ZapState,
  WheelState,
  UserCollectionsState,
} from './types'
import { transformPool } from './pools/helpers'
import { fetchFarmUserDataAsync, nonArchivedFarms } from './farms'

export const usePollFarmsData = (includeArchive = false) => {
  const dispatch = useAppDispatch()
  const { slowRefresh } = useRefresh()
  const { account } = useWeb3React()

  useEffect(() => {
    const farmsToFetch = includeArchive ? farmsConfig : nonArchivedFarms
    const pids = farmsToFetch.map((farmToFetch) => farmToFetch.pid)

    dispatch(fetchFarmsPublicDataAsync(pids))

    if (account) {
      dispatch(fetchFarmUserDataAsync({ account, pids }))
    }
  }, [includeArchive, dispatch, slowRefresh, account])
}

export const useDataFromApi = () => {
  const dispatch = useAppDispatch()
  const { account } = useWeb3React()

  useEffect(() => {
    dispatch(fetchPublicDataFromApi())

    if (account) {
      // dispatch(fetchUserDataFromApi(account))
    }
  }, [account, dispatch])
}

/**
 * Fetches the "core" farm data used globally
 */
export const usePollCoreFarmData = () => {
  const dispatch = useAppDispatch()
  const { fastRefresh } = useRefresh()

  useEffect(() => {
    dispatch(fetchFarmsPublicDataAsync([1]))
  }, [dispatch, fastRefresh])
}

export const usePollBlockNumber = () => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    const interval = setInterval(async () => {
      const blockNumber = await simpleRpcProvider.getBlockNumber()
      dispatch(setBlock(blockNumber))
    }, 6000)

    return () => clearInterval(interval)
  }, [dispatch])
}

// Farms

export const useFarms = (): FarmsState => {
  const farms = useSelector((state: State) => state.farms)
  return farms
}

export const useFarmFromPid = (pid: number): Farm => {
  const farm = useSelector((state: State) => state.farms.data.find((f) => f.pid === pid))
  return farm
}

export const useFarmFromLpSymbol = (symbol: string): Farm => {
  const farm = useSelector((state: State) => state.farms.data.find((f) => f.symbol === symbol))
  return farm
}

export const useFarmUser = (pid) => {
  const farm = useFarmFromPid(pid)

  return {
    allowance: farm.userData ? new BigNumber(farm.userData.allowance) : BIG_ZERO,
    tokenBalance: farm.userData ? new BigNumber(farm.userData.tokenBalance) : BIG_ZERO,
    stakedBalance: farm.userData ? new BigNumber(farm.userData.stakedBalance) : BIG_ZERO,
    earnings: farm.userData ? new BigNumber(farm.userData.earnings) : BIG_ZERO,
  }
}

// Return a farm for a given token symbol. The farm is filtered based on attempting to return a farm with a quote token from an array of preferred quote tokens
export const useFarmFromTokenSymbol = (tokenSymbol: string, preferredQuoteTokens?: string[]): Farm => {
  const farms = useSelector((state: State) => state.farms.data.filter((farm) => farm.token.symbol === tokenSymbol))
  const filteredFarm = filterFarmsByQuoteToken(farms, preferredQuoteTokens)
  return filteredFarm
}

// Return the base token price for a farm, from a given pid
export const useBusdPriceFromPid = (pid: number): BigNumber => {
  const farm = useFarmFromPid(pid)
  return farm && new BigNumber(farm.token.usdcPrice)
}

export const useBusdPriceFromToken = (tokenSymbol: string): BigNumber => {
  const tokenFarm = useFarmFromTokenSymbol(tokenSymbol)
  const tokenPrice = useBusdPriceFromPid(tokenFarm?.pid)
  return tokenPrice
}

export const useFarmPrices = () => {
  const data = useSelector((state: State) => state.farms.data)
  return data
}

export const useLpTokenPrice = (symbol: string) => {
  const farm = useFarmFromLpSymbol(symbol)
  const farmTokenPriceInUsd = useBusdPriceFromPid(farm.pid)
  let lpTokenPrice = BIG_ZERO

  if (farm.lpTotalSupply && farm.lpTotalInQuoteToken) {
    // Total value of base token in LP
    const valueOfBaseTokenInFarm = farmTokenPriceInUsd.times(farm.tokenAmountTotal)
    // Double it to get overall value in LP
    const overallValueOfAllTokensInFarm = valueOfBaseTokenInFarm.times(2)
    // Divide total value of all tokens, by the number of LP tokens
    const totalLpTokens = getBalanceAmount(new BigNumber(farm.lpTotalSupply))
    lpTokenPrice = overallValueOfAllTokensInFarm.div(totalLpTokens)
  }

  return lpTokenPrice
}

// Presale

export const useFetchPublicPresaleData = () => {
  const dispatch = useAppDispatch()
  const { slowRefresh } = useRefresh()

  useEffect(() => {
    const fetchPresalePublicData = async () => {
      dispatch(fetchPresalePublicDataAsync())
    }

    fetchPresalePublicData()
  }, [dispatch, slowRefresh])
}

export const usePresale = (account): { presale: PresaleState; userDataLoaded: boolean } => {
  const { fastRefresh } = useRefresh()
  const dispatch = useAppDispatch()
  useEffect(() => {
    if (account) {
      dispatch(fetchUserPresaleDataAsync(account))
    }
  }, [account, dispatch, fastRefresh])

  const { presale, userDataLoaded } = useSelector((state: State) => ({
    presale: state.presale,
    userDataLoaded: false,
  }))
  return { presale, userDataLoaded }
}

// Pools

export const useFetchPublicPoolsData = () => {
  const dispatch = useAppDispatch()
  const { slowRefresh } = useRefresh()
  const farmPrices = useFarmPrices()

  useEffect(() => {
    const fetchPoolsPublicData = async () => {
      dispatch(fetchPoolsPublicDataAsync())
    }

    fetchPoolsPublicData()
  }, [dispatch, slowRefresh, farmPrices])
}

export const usePools = (account): { pools: Pool[]; userDataLoaded: boolean } => {
  const { fastRefresh } = useRefresh()
  const dispatch = useAppDispatch()
  useEffect(() => {
    if (account) {
      dispatch(fetchPoolsUserDataAsync(account))
    }
  }, [account, dispatch, fastRefresh])

  const { pools, userDataLoaded } = useSelector((state: State) => ({
    pools: state.pools.data,
    userDataLoaded: state.pools.userDataLoaded,
  }))
  return { pools: pools.map(transformPool), userDataLoaded }
}

export const usePoolFromPid = (sousId: number): Pool => {
  const pool = useSelector((state: State) => state.pools.data.find((p) => p.sousId === sousId))
  return transformPool(pool)
}

export const usePriceDmdUsdc = (): BigNumber => {
  const dmdUsdcFarm = useFarmFromPid(4)
  return new BigNumber(dmdUsdcFarm.token.usdcPrice)
  // TODO: Change back once LP token gets added to the MasterChef
  // return new BigNumber(1)
}

// Multi Pool

export const useFetchPublicMultiPoolData = () => {
  const dispatch = useAppDispatch()
  const { fastRefresh } = useRefresh()

  useEffect(() => {
    const fetchMultiPoolPublicData = async () => {
      dispatch(fetchMultiPoolPublicDataAsync())
    }

    fetchMultiPoolPublicData()
  }, [dispatch, fastRefresh])
}

export const useMultiPool = (): MultiPoolState => {
  const { fastRefresh } = useRefresh()
  const dispatch = useAppDispatch()
  const { account } = useWeb3React()

  useEffect(() => {
    if (account) {
      dispatch(fetchMultiPoolUserDataAsync(account))
    }
  }, [account, dispatch, fastRefresh])

  const multiPoolState = useSelector((state: State) => state.multiPool)

  return multiPoolState
}

// Launchpad

export const useFetchLaunchpadData = () => {
  const dispatch = useAppDispatch()
  const { fastRefresh } = useRefresh()
  const { account } = useWeb3React()

  useEffect(() => {
    dispatch(fetchLaunchpadPublicDataAsync())

    if (account) {
      dispatch(fetchLaunchpadUserDataAsync(account))
    }
  }, [account, dispatch, fastRefresh])
}

export const useLaunchpad = (): LaunchpadState => {
  const launchpadState = useSelector((state: State) => state.launchpad)

  return launchpadState
}

// Block
export const useBlock = () => {
  return useSelector((state: State) => state.block)
}

export const useInitialBlock = () => {
  return useSelector((state: State) => state.block.initialBlock)
}

// Vaults
export const useVaults = (): VaultsState => {
  const vaults = useSelector((state: State) => state.vaults)
  return vaults
}

export const usePollVaultsData = () => {
  const dispatch = useAppDispatch()
  const { fastRefresh } = useRefresh()
  const { account } = useWeb3React()

  useEffect(() => {
    const vaultsToFetch = vaultsConfig
    const vids = vaultsToFetch.map((vaultToFetch) => vaultToFetch.vid)

    dispatch(fetchVaultsPublicDataAsync(vids))

    if (account) {
      dispatch(fetchVaultUserDataAsync({ account, vids }))
    }
  }, [dispatch, fastRefresh, account])
}

// NFT Serieses

export const useSerieses = (): SeriesesState => {
  const serieses = useSelector((state: State) => state.serieses)
  return serieses
}

export const useFetchSeriesesData = () => {
  const dispatch = useAppDispatch()
  const { account } = useWeb3React()
  const { fastRefresh } = useRefresh()

  useEffect(() => {
    dispatch(fetchSeriesesPublicDataAsync())

    if (account) {
      dispatch(fetchSeriesesUserDataAsync(account))
    }
  }, [account, dispatch, fastRefresh])
}

export const useFetchTokenBalancesData = () => {
  const dispatch = useAppDispatch()
  const { account } = useWeb3React()
  const { fastRefresh } = useRefresh()

  useEffect(() => {
    const fetchTokenBalancesPublicData = async () => {
      if (account) {
        dispatch(fetchTokenBalancesUserDataAsync(account))
      }
    }

    fetchTokenBalancesPublicData()
  }, [dispatch, account, fastRefresh])
}

export const useZap = (): ZapState => {
  const { tokenBalances, userDataLoaded } = useSelector((state: State) => ({
    tokenBalances: state.zap.tokenBalances,
    userDataLoaded: state.zap.userDataLoaded,
  }))

  return {
    tokenBalances,
    userDataLoaded,
  }
}

// Wheel

export const useFetchWheelData = () => {
  const dispatch = useAppDispatch()
  const { fastRefresh } = useRefresh()
  const { account } = useWeb3React()

  useEffect(() => {
    dispatch(fetchWheelPublicDataAsync())

    if (account) {
      dispatch(fetchWheelUserDataAsync(account))
    }
  }, [account, dispatch, fastRefresh])
}

export const useWheel = (): WheelState => {
  const wheelState = useSelector((state: State) => state.wheel)

  return wheelState
}

// UserCollections

export const useFetchUserCollectionsData = () => {
  const dispatch = useAppDispatch()
  const { fastRefresh } = useRefresh()
  const { account } = useWeb3React()

  useEffect(() => {
    if (account) {
      dispatch(fetchUserCollectionsDataAsync(account))
    }
  }, [account, dispatch, fastRefresh])
}

export const useUserCollections = (): UserCollectionsState => {
  const userCollections = useSelector((state: State) => state.userCollections)

  return userCollections
}
